import {memo, useEffect, useMemo} from 'react'
import {useParams} from 'react-router-dom'
import { Divider, Input } from 'antd'
// import dayjs from 'dayjs'
import Big from 'big.js'
import cn from 'clsx'
import cardStyles from './AboutCard.variation.module.scss'
import styles from './RequestForecast.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
// import {useInputFocus} from 'common/hooks'
import {
  Block,
  Text,
  Modal,
  Button, IconButton,
  DotsIcon,
  Form, useForm, useWatch, FormItem, InputAmount, InputPercent,
} from 'common/components'
// import {useGetForecast} from 'common/api'
import { isEqual } from 'common/helpers'
import {FIELD_RULES} from 'common/constants'
import {VariationScheduleList} from '..'
import {useGetVariationRequest, useGetVariationSchedule, useSetVariationEstablishmentFee} from '../api.variation'
// import {useGetProjectJournal} from '../api.journal'
// import { SCHEDULE_DATA_ESTABLISHMENT_FEE, SCHEDULE_DATA_JOURNAL_INPUT, } from './VariationScheduleList.mock'
import {isSameTerm, isReduceAmount} from 'modules/project/components/VariationForm.helper'
import { 
  formatDate, formatMoney,
  cbFilterSchedule, cbFilterJournal, cbMap2Schedule, cbMap2Journal 
} from '../schedule.helper'
const {TextArea} = Input

const amountFieldRules = [
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      if (
        (!!value) || (value === 0)
      ) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
  }),
]

interface CardProps {
  journalIncluded: boolean
  restQuota: number
  balance: number
  request: any
  isConfirm?: boolean
  onModalOpen?: () => void
}
interface FormProps {
  isDuringConfirm: boolean
  isConfirm?: boolean
  balance: number
  restQuota: number
  increaseAmount: number
  request: any
  onClose: () => void
}

export default function VariationRequestEstablishmentFee({
  isDuringConfirm, isConfirm, showCard,
}:{
  isDuringConfirm?: boolean     // it is during the 2nd round right now
  isConfirm?: boolean           // this component is for the 2nd round tasks
  showCard?: boolean
}) {
  const params = useParams()
  const {data:request} = useGetVariationRequest(params.uuid)
  const {data} = useGetVariationSchedule(params.uuid, 'establishFee')
  // const {data: journal} = useGetProjectJournal(request?.project?.uuid, !!request?.project?.uuid)
  const { originalUndrawAmount, balance} = data || {}
  const scheduleItems = data?.scheduleItems?.filter(
    cbFilterSchedule
  ).map(cbMap2Schedule) || []
  // const journalItems: any[] = []
  const journalItems = data?.scheduleItems?.filter(
    cbFilterJournal
  ).map(cbMap2Journal) || []

  const modalOpenKey   = useStore((state) => state.variationEstablishmentFeeOpenKey)
  const setModalOpenKey= useStore((state) => state.setVariationEstablishmentFeeOpenKey)
  const handleModalOpen = () => setModalOpenKey(!isConfirm ? '1st':'2nd')
  const handleModalClose = () => setModalOpenKey(null)

  const lastDate = formatDate(request?.startDate)

  return (
    <>
      { !!showCard &&
      <Block relative type="white">
        <Card isConfirm={isConfirm}
          request={request}
          journalIncluded={!!journalItems?.length}
          restQuota={originalUndrawAmount} 
          balance={balance} 
          onModalOpen={handleModalOpen}
        />
      </Block>
      }
      <Modal
        // title={`Variation Forecast`}
        width={1392}
        open={modalOpenKey === (!isConfirm ? '1st':'2nd') }
        onCancel={handleModalClose}
      >
        <Text mt={48} mb={16} size="2xl" weight="bold" color="black">Establishment fee</Text>
        <Divider />
        <Text mb={40} size="md" color="black">
          To calculate the establishment fee, we require confirmation of the balance from the day prior to the variation day. The system calculates the balance based on the schedule as following. However, if necessary, you can add a journal entry to adjust the balance according to the contract.
        </Text>
        <VariationScheduleList
          permissionRequired='edit variation establishment fee'
          statusRequired={ !isConfirm ? 'lending-manager-check':'ready-to-open'}
          title={`Schedule (Until ${lastDate})`}
          exTitle='establishment-fee'
          request={request}
          data={[
            // ...SCHEDULE_DATA_ESTABLISHMENT_FEE,
            ...scheduleItems,
          ]}
          journal={[
            // ...SCHEDULE_DATA_JOURNAL_INPUT
            ...journalItems
          ]} 
        />
        <Divider />
        <ConfirmForm
          isDuringConfirm={isDuringConfirm}
          isConfirm={isConfirm}
          balance={balance} 
          restQuota={originalUndrawAmount} 
          increaseAmount={request?.increaseAmount ?? 0} 
          request={request}
          onClose={handleModalClose}
        />
      </Modal>
    </>
  )
}

const Card = memo(
  ({onModalOpen, journalIncluded, request, restQuota, balance, isConfirm}: CardProps) => {
    
    const increaseAmount  = request?.increaseAmount ?? 0
    const confirmedFee    = request?.establishFeeAmount ?? 0
    const legalFee        = (!isConfirm ? request?.legalFeeAmount   : request?.legalFeeAmountConfirmed ) ?? 0
    const brokerFee       = (!isConfirm ? request?.brokerFeeAmount  : request?.brokerFeeAmountConfirmed ) ?? 0
    const otherFee        = (!isConfirm ? request?.otherFeeAmount   : request?.otherFeeAmountConfirmed ) ?? 0
    const defaultRate     = request?.establishFee ?? 1

    const [estFeeAmount] = !!isConfirm ? [request?.establishFeeAmount || 0, request.establishFee || 0] : calcEstablishment(
      request?.type,
      increaseAmount + legalFee + brokerFee + otherFee,
      balance, restQuota,
      defaultRate, 0,
      defaultRate
    )
    const diff = (confirmedFee ?? 0) - (estFeeAmount ?? 0)

    return (
      <>
        { !!onModalOpen &&
          <IconButton className={styles.edit} onClick={onModalOpen}>
            <DotsIcon />
          </IconButton>
        }

        <div className={cardStyles.root}>
          <Text size="md" weight="demiBold" color="black" mb={!journalIncluded ? 22 : 16}>
            Establishment fee
          </Text>
          { !!journalIncluded &&
            <Text size="xs" color="black" mb={16}>
              *Credit journal entry has been added to the schedule.
            </Text>
          }

          <div className={cardStyles['grid-columns-3']}>
            <div>
              <Text color="grayer" size="xs" mb={4}>System estimated</Text>
              <Text color="black"  size="md" weight="medium">
                {formatMoney(estFeeAmount, '0')}
              </Text>
            </div>
            <div>
              <Text color="grayer" size="xs" mb={4}>Confirmed</Text>
              <Text color="black"  size="md" weight="medium">
                {formatMoney(confirmedFee, '0')}
              </Text>
            </div>
            <div>
              <Text color="grayer" size="xs" mb={4}>Differences</Text>
              <Text color="brown" size="md" weight="medium">
                {`${(diff>0) ? '+':''}${formatMoney(diff)}`}
              </Text>
            </div>
          </div>

          <Text mt={12} mb={4} color="grayer" size="xs">Comments</Text>
          <Text color="black" size="xs" >
            { !isConfirm ? request.establishFeeComment:request.establishFeeCommentConfirmed }
          </Text>
        </div>
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
)

const calcEstablishment = (
  type: string, 
  baseTotal: number, balance: number, restQuota: number,
  rateValue: number, estFeeValue: number,
  defaultRate: number
) => {
  const subTotal =  ( isSameTerm(type) && isReduceAmount(type) )  ? 0 :
                    ( isSameTerm(type) )                          ? (baseTotal) :
                    (!isSameTerm(type))                           ? (baseTotal + balance + restQuota) :
                    0
  if (isNaN(subTotal) || isNaN(rateValue)) return [0, 0]

  const estFeeAmount = Big(subTotal ?? 0).times(rateValue ?? 0).div(100).round(2, Big.roundHalfUp).toNumber()
  const realRate = (!!subTotal) ? (100 * estFeeValue / subTotal) : defaultRate
  return [estFeeAmount, realRate]
}

const ConfirmForm = ({balance, restQuota, increaseAmount, request, onClose, isConfirm, isDuringConfirm}: FormProps) => {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const {mutate: setEstFee} = useSetVariationEstablishmentFee(request.uuid, onClose)
  
  const [form] = useForm()
  // const inputRef = useInputFocus()
  const estFeeValue = useWatch('establishmentFee', form)
  const legalFeeValue = useWatch('legalFee', form)
  const brokerFeeValue = useWatch('brokerFee', form)
  const otherFeeValue = useWatch('otherFee', form)
  const rateValue = useWatch('rate', form)

  const handleFinish = (values: any) => {
    const payload = {
      legalFeeAmount: values.legalFee,
      brokerFeeAmount: values.brokerFee,
      otherFeeAmount: values.otherFee,
      establishFee: values.rate,
      establishFeeAmount: values.establishmentFee,
      comment: values.comments
    }
    setEstFee(payload)
  }

  const [estFeeAmount, realRate] = !!isConfirm ? [request?.establishFeeAmount || 0, request.establishFee || 0] : calcEstablishment(
    request?.type,
    increaseAmount + legalFeeValue + brokerFeeValue + otherFeeValue,
    balance, restQuota,
    rateValue, estFeeValue,
    (request?.establishFee ?? 1)
  )

  // console.log(request)
  // console.log(estFeeAmount, legalFeeValue, brokerFeeValue, rateValue)

  const initialValues: any = useMemo(() => ({
    legalFee  : (!!isConfirm ? (request?.legalFeeAmountConfirmed  || request?.legalFeeAmount) : request?.legalFeeAmount) ?? 0,
    brokerFee : (!!isConfirm ? (request?.brokerFeeAmountConfirmed || request?.brokerFeeAmount): request?.brokerFeeAmount) ?? 0,
    otherFee  : (!!isConfirm ? (request?.otherFeeAmountConfirmed  || request?.otherFeeAmount) : request?.otherFeeAmount) ?? 0,
    comments  : (!!isConfirm ? (request?.establishFeeCommentConfirmed || request?.establishFeeComment): request?.establishFeeComment) || '',
    
    rate      : (request?.establishFee) ?? 1,
    establishmentFee: request?.establishFeeAmount || estFeeAmount || 0,
  }), [isConfirm, request, estFeeAmount])

  useEffect(() => {
    if (form.isFieldTouched('establishmentFee')) return
    // else
    const curValue = form.getFieldValue('establishmentFee')
    const initValue= initialValues.establishmentFee
    if (curValue !== initValue) {
      form.setFieldValue('establishmentFee', initValue)
    }
  }, [initialValues, form])

  const statusRequired= !isConfirm ? 'lending-manager-check':'ready-to-open'
  const isNotAllowed = (statusRequired !== request?.status) || (!permissions?.includes('edit variation establishment fee'))
  const disableInput = (!isConfirm && !!isDuringConfirm) || isNotAllowed
  
  return (
    <>
      <Text mt={52} mb={40} size="lg" color='black' weight="bold">Establishment Fee</Text>
      <Form className={cn(styles['confirm-form'], styles['confirm-form-pr'])}
        initialValues={initialValues}
        form={form}
        validateTrigger="onChange"
        // onValuesChange={handleFieldsChange}
        onFinish={handleFinish}
      >
        <div className={styles['grid-columns-7']}>
          <FormAmountViewer label="The balance before variation" amount={balance} />
          <FormAmountViewer label="The amount to be increased" amount={increaseAmount} />
          <FormAmountViewer label="Original undraw down amount" amount={restQuota} />
          <FormNumberInput disabled={disableInput} name="legalFee" label="Legal Fee" rules={amountFieldRules} min={0} />
          <FormNumberInput disabled={disableInput} name="brokerFee" label="Broker Fee" rules={amountFieldRules} min={0} />
          <FormNumberInput disabled={disableInput} name="otherFee" label="Other Fee" rules={amountFieldRules} min={0} />
          <FormNumberInput disabled={disableInput} type="rate" name="rate" label="Estab. fee rate" rules={FIELD_RULES} min={0} />
        </div>

        <Text mt={40} mb={48} size="md" color='black'>
          Based on the provided input, the estimated establishment fee is
          &nbsp;
          <span className={styles['confirm-highlight']}>{formatMoney(estFeeAmount, '0')}</span>,
          &nbsp;
          for your reference, please input the final value accordingly.
        </Text>

        <div className={styles['grid-columns-3b']}>
          <FormNumberInput disabled={!!isConfirm || disableInput} name="establishmentFee" label="Establishment fee" rules={FIELD_RULES} min={0} />
          <FormAmountViewer label="Differences with the estimated value" amount={
            !!estFeeValue ? (estFeeValue - estFeeAmount) : 0
          } color="brown" />
          <FormAmountViewer label="Real Establishment fee rate" amount={realRate} postFix='%' />
        </div>

        <div style={{width: '792px'}}>
          <FormItem name="comments" label="Comments" rules={FIELD_RULES}>
            <TextArea disabled={disableInput} rows={7} placeholder='Please enter comments for review...' />
          </FormItem>
        </div>
        <div className={styles['flex-center']}>
        { !disableInput &&
          <Button // block
            htmlType="submit"
            loading={
              false
              // isCreateMutating || isUpdateMutating || isUpdateForecastMutating
            }
          >
            Confirm
          </Button>
        }
        </div>
      </Form>
    </>
  )
}

const FormAmountViewer = ({color, postFix, label, amount}:{
  label: string;
  amount: number;
  color?: 'brown';
  postFix?: string;
}) => {
  return (
    <div>
      <Text mb={6} size="xs" color="grayer">{label}</Text>
      <div style={{height: '48px', display: 'flex', alignItems: 'center'}}>
        <Text size="md" color={color || "black"} weight="medium">
          { !postFix ? 
            formatMoney(amount) : 
            `${formatMoney(amount).replace('$', '')}%`
          }
        </Text>
      </div>
    </div>
  )
}

const FormNumberInput = ({type, name, label, rules, min, disabled}:{
  // ref?: any;
  min: number;
  name: string;
  label: string;
  rules: any;
  type?: 'money' | 'rate';
  disabled?: boolean;
}) => (
  <div>
    <FormItem name={name} label={label} rules={rules}>
    { (type !== 'rate') ?
      <InputAmount disabled={disabled} min={min} />
      :
      <InputPercent disabled={disabled} />
    }
    </FormItem>
  </div>
)
